// @figma file=pkCGby54zXzTsccXvHqzO6 node=3853:15460 name="Switch" set="mytaible-shadcn-studio" verified=2026-03-21

import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * CVA variants for the Switch track (root element).
 *
 * Checked background is NOT in the base — it lives entirely in compound
 * variants so `appearance` can override without a specificity fight.
 */
const switchVariants = cva(
  [
    // ── Layout & shape ──────────────────────────────────────────────────────
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full',
    // ── Border (transparent; outline appearance overrides per-state) ─────────
    'border-2 border-transparent',
    // ── Transition ──────────────────────────────────────────────────────────
    'transition-colors duration-200 ease-in-out',
    // ── Focus ring ──────────────────────────────────────────────────────────
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    // ── Unchecked track ─────────────────────────────────────────────────────
    'bg-input',
    // ── Disabled ────────────────────────────────────────────────────────────
    'disabled:cursor-not-allowed disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      // ── Size — track dimensions ────────────────────────────────────────────
      size: {
        xs: 'h-3 w-6',
        sm: 'h-4 w-8',
        md: 'h-5 w-9',
        lg: 'h-6 w-11',
      },
      // ── Variant — semantic colour (focus ring + checked track/thumb tint) ──
      variant: {
        default: 'focus-visible:ring-ring',
        destructive: 'focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        success: 'focus-visible:ring-green-600/20 dark:focus-visible:ring-green-400/40',
        info: 'focus-visible:ring-sky-600/20 dark:focus-visible:ring-sky-400/40',
        warning: 'focus-visible:ring-amber-600/20 dark:focus-visible:ring-amber-400/40',
      },
      // ── Appearance — how the checked state is rendered ─────────────────────
      appearance: {
        // Solid fill — track background changes colour when checked
        filled: '',
        // Outline — track stays transparent; border and thumb gain colour
        outline: [
          '[&_span]:border',
          'data-[state=checked]:bg-transparent',
          'data-[state=checked]:[&_span]:border-background',
        ].join(' '),
        // Gradient — amber → sky/indigo transition when toggled
        gradient: 'border-none bg-linear-to-r from-amber-500 to-amber-500/60',
      },
      // ── Shape ─────────────────────────────────────────────────────────────
      shape: {
        round: '',
        square: [
          'rounded-[var(--field-radius,var(--radius))]',
          '[&_span]:rounded-[var(--field-radius,var(--radius))]',
        ].join(' '),
      },
    },
    compoundVariants: [
      // ── filled: track checked colour ──────────────────────────────────────
      { appearance: 'filled', variant: 'default', class: 'data-[state=checked]:bg-primary' },
      {
        appearance: 'filled',
        variant: 'destructive',
        class: 'data-[state=checked]:bg-destructive',
      },
      {
        appearance: 'filled',
        variant: 'success',
        class: 'data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-400',
      },
      {
        appearance: 'filled',
        variant: 'info',
        class: 'data-[state=checked]:bg-sky-600 dark:data-[state=checked]:bg-sky-400',
      },
      {
        appearance: 'filled',
        variant: 'warning',
        class: 'data-[state=checked]:bg-amber-600 dark:data-[state=checked]:bg-amber-400',
      },
      // ── outline: track border + thumb colour when checked ─────────────────
      {
        appearance: 'outline',
        variant: 'default',
        class: 'data-[state=checked]:border-primary data-[state=checked]:[&_span]:bg-primary',
      },
      {
        appearance: 'outline',
        variant: 'destructive',
        class:
          'data-[state=checked]:border-destructive data-[state=checked]:[&_span]:bg-destructive',
      },
      {
        appearance: 'outline',
        variant: 'success',
        class: [
          'data-[state=checked]:border-green-600 dark:data-[state=checked]:border-green-400',
          'data-[state=checked]:[&_span]:bg-green-600 dark:data-[state=checked]:[&_span]:bg-green-400',
        ].join(' '),
      },
      {
        appearance: 'outline',
        variant: 'info',
        class: [
          'data-[state=checked]:border-sky-600 dark:data-[state=checked]:border-sky-400',
          'data-[state=checked]:[&_span]:bg-sky-600 dark:data-[state=checked]:[&_span]:bg-sky-400',
        ].join(' '),
      },
      {
        appearance: 'outline',
        variant: 'warning',
        class: [
          'data-[state=checked]:border-amber-600 dark:data-[state=checked]:border-amber-400',
          'data-[state=checked]:[&_span]:bg-amber-600 dark:data-[state=checked]:[&_span]:bg-amber-400',
        ].join(' '),
      },
      // ── gradient: checked gradient colours ────────────────────────────────
      {
        appearance: 'gradient',
        variant: 'default',
        class: 'data-[state=checked]:from-sky-400 data-[state=checked]:to-indigo-700',
      },
    ],
    defaultVariants: { size: 'md', variant: 'default', appearance: 'filled', shape: 'round' },
  },
);

/** CVA variants for the Switch thumb (sliding indicator). */
const switchThumbVariants = cva(
  [
    'pointer-events-none block rounded-full bg-background shadow-lg ring-0',
    'transition-transform duration-200 ease-in-out',
  ].join(' '),
  {
    variants: {
      size: {
        // translate = inner-track-width − thumb-width
        // inner = track-w − 2×border(2px) = track-w − 4px
        xs: 'h-2 w-2 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0',
        sm: 'h-3 w-3 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        md: 'h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
        lg: 'h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

// ─── Switch ───────────────────────────────────────────────────────────────────

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    VariantProps<typeof switchVariants> {
  /** Controlled on/off state. */
  checked?: boolean;
  /** Callback fired when the checked state changes. */
  onCheckedChange?: (checked: boolean) => void;
  /** Prevents interaction and dims the control. */
  disabled?: boolean;
  /** Forwarded to the hidden form input. */
  required?: boolean;
  /** Forwarded to the hidden form input. */
  name?: string;
  /** Forwarded to the hidden form input. */
  value?: string;
  /**
   * Visual size.
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * Semantic colour applied to the checked state.
   * @default 'default'
   */
  variant?: 'default' | 'destructive' | 'success' | 'info' | 'warning';
  /**
   * How the checked state is rendered.
   * - `filled` (default) — track fills with the variant colour.
   * - `outline` — track stays transparent; border and thumb gain the variant colour.
   * - `gradient` — amber-to-transparent track transitions to sky→indigo when on.
   * @default 'filled'
   */
  appearance?: 'filled' | 'outline' | 'gradient';
  /**
   * Track and thumb corner shape.
   * @default 'round'
   */
  shape?: 'round' | 'square';
  /** Additional CSS classes merged onto the root element. */
  className?: string;
}

/**
 * On/off boolean toggle built on Radix `SwitchPrimitive`.
 *
 * Renders the ARIA `switch` role with `aria-checked` managed automatically.
 * Keyboard accessible: press `Space` to toggle.
 *
 * @example
 * // Controlled with semantic colour
 * <Switch checked={enabled} onCheckedChange={setEnabled} variant="success" />
 *
 * @example
 * // Outline appearance
 * <Switch appearance="outline" variant="destructive" />
 *
 * @example
 * // Gradient appearance
 * <Switch appearance="gradient" />
 *
 * @example
 * // With label (recommended for accessibility)
 * <div className="flex items-center gap-2">
 *   <Switch id="notifications" />
 *   <label htmlFor="notifications">Enable notifications</label>
 * </div>
 */
const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, size, variant, appearance, shape, ...props }, ref) => (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(switchVariants({ size, variant, appearance, shape }), className)}
      {...props}
    >
      <SwitchPrimitive.Thumb className={cn(switchThumbVariants({ size }))} />
    </SwitchPrimitive.Root>
  ),
);
Switch.displayName = 'Switch';

// ─── SwitchWithIconIndicators ──────────────────────────────────────────────────

export interface SwitchWithIconIndicatorsProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Additional classes for the outer wrapper element. */
  className?: string;
}

/**
 * Switch with ✓ and ✕ icons inside the track that fade based on toggle state.
 *
 * Both icons are always present; the active-state icon is fully opaque, the
 * inactive-state icon dims to `muted-foreground/70`. Uses a fixed medium size.
 *
 * Inspired by shadcnstudio Switch 12.
 *
 * @example
 * <SwitchWithIconIndicators aria-label="Enable feature" />
 */
const SwitchWithIconIndicators = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchWithIconIndicatorsProps
>(({ className, ...props }, ref) => (
  <div className={cn('relative inline-grid h-7 grid-cols-[1fr_1fr] items-center', className)}>
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        // Fill the container grid cell; icons sit alongside in sibling cells
        'peer absolute inset-0 h-[inherit] w-14',
        'cursor-pointer rounded-full border-2 border-transparent',
        // Same low-contrast bg in both states so icons read clearly
        'bg-input/50 data-[state=checked]:bg-input/50',
        'transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-6 rounded-full bg-background shadow-lg ring-0 z-10',
          'transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          // inner-width(52px) − thumb(24px) = 28px = translate-x-7
          'data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0',
        )}
      />
    </SwitchPrimitive.Root>

    {/* ✓ — dims when unchecked (switch is off) */}
    <span className="peer-data-[state=checked]:text-muted-foreground/70 pointer-events-none relative ml-1.5 flex min-w-7 items-center justify-center">
      <Check className="size-4" aria-hidden="true" />
    </span>

    {/* ✕ — dims when checked (switch is on) */}
    <span className="peer-data-[state=unchecked]:text-muted-foreground/70 pointer-events-none relative flex min-w-7 items-center justify-center">
      <X className="size-4" aria-hidden="true" />
    </span>
  </div>
));
SwitchWithIconIndicators.displayName = 'SwitchWithIconIndicators';

// ─── SwitchWithSlidingIndicators ──────────────────────────────────────────────

export interface SwitchWithSlidingIndicatorsProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Additional classes for the outer wrapper element. */
  className?: string;
}

/**
 * Switch where ✓ and ✕ icons **slide** with the thumb animation.
 *
 * - Unchecked → ✕ visible in track, ✓ hidden off-screen to the right.
 * - Checked → ✓ slides in from right, ✕ slides out to the right and vanishes.
 *
 * Uses a fixed medium size. Inspired by shadcnstudio Switch 13.
 *
 * @example
 * <SwitchWithSlidingIndicators aria-label="Enable feature" />
 */
const SwitchWithSlidingIndicators = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchWithSlidingIndicatorsProps
>(({ className, ...props }, ref) => (
  <div className={cn('relative inline-grid h-7 grid-cols-[1fr_1fr] items-center', className)}>
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'peer absolute inset-0 h-[inherit] w-14',
        'cursor-pointer rounded-full border-2 border-transparent',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input/50',
        'transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-6 rounded-full bg-background shadow-lg ring-0 z-10',
          'transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          'data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0',
        )}
      />
    </SwitchPrimitive.Root>

    {/* ✕ — visible when unchecked; slides right out of view when checked */}
    <span
      className={cn(
        'pointer-events-none relative ml-0.5 flex min-w-8 items-center justify-center',
        'transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        'peer-data-[state=checked]:invisible peer-data-[state=unchecked]:translate-x-6',
      )}
    >
      <X className="size-4" aria-hidden="true" />
    </span>

    {/* ✓ — invisible when unchecked; slides left into view and turns white when checked */}
    <span
      className={cn(
        'pointer-events-none relative flex min-w-8 items-center justify-center',
        'transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        'peer-data-[state=checked]:text-background',
        'peer-data-[state=checked]:-translate-x-full peer-data-[state=unchecked]:invisible',
      )}
    >
      <Check className="size-4" aria-hidden="true" />
    </span>
  </div>
));
SwitchWithSlidingIndicators.displayName = 'SwitchWithSlidingIndicators';

// ─── SwitchCard ───────────────────────────────────────────────────────────────

export interface SwitchCardProps extends Omit<SwitchProps, 'className' | 'children'> {
  /** Must be unique on the page — links the `<label>` and `aria-describedby`. */
  id: string;
  /** Primary label text rendered next to the icon. */
  label: React.ReactNode;
  /** Optional description rendered below the label. */
  description?: React.ReactNode;
  /** Optional leading icon or image. */
  icon?: React.ReactNode;
  /** Additional classes for the card container element. */
  className?: string;
}

/**
 * Card-style toggle control with label, optional description, and optional icon.
 *
 * The entire card surface is clickable via an `after:` pseudo-element on the
 * Switch. The card border highlights (`border-primary/50`) when checked via
 * CSS `:has()`. Uses the `xs` Switch size to keep visual weight proportional.
 *
 * Inspired by shadcnstudio Switches 15–17.
 *
 * @example
 * <SwitchCard
 *   id="backup"
 *   label="Automatic backup"
 *   description="Back up every file from your project."
 *   icon={<DatabaseIcon className="size-5" />}
 * />
 */
const SwitchCard = React.forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchCardProps>(
  ({ id, label, description, icon, className, ...switchProps }, ref) => (
    <div
      className={cn(
        'border-input has-data-[state=checked]:border-primary/50',
        'relative flex w-full items-start gap-2 rounded-md border p-4 shadow-xs',
        className,
      )}
    >
      <Switch
        ref={ref}
        id={id}
        size="xs"
        // `after:` pseudo-element covers the card, making the whole surface clickable
        className="order-1 after:absolute after:inset-0"
        aria-describedby={description ? `${id}-description` : undefined}
        {...switchProps}
      />
      <div className={cn('flex grow gap-3', icon ? 'items-start' : 'items-center')}>
        {icon}
        <div className="grid grow gap-1">
          <label htmlFor={id} className="cursor-pointer text-sm leading-none font-medium">
            {label}
          </label>
          {description && (
            <p id={`${id}-description`} className="text-muted-foreground text-xs">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  ),
);
SwitchCard.displayName = 'SwitchCard';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Switch,
  SwitchCard,
  SwitchWithIconIndicators,
  SwitchWithSlidingIndicators,
  switchThumbVariants,
  switchVariants,
};
